import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState, type ComponentType, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useUnreadMessageCount } from "@/hooks/useMessages";
import { isAdmin } from "@/hooks/useIsAdmin";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import { Logo } from "./Logo";
import { IdleWarningDialog } from "./IdleWarningDialog";
import {
  HomeIcon,
  PillOutlineIcon,
  ChatIcon,
  TrendIcon,
  CalendarIcon,
  LabIcon,
  CardIcon,
  UserIcon,
  HelpCircleIcon,
  SyringeIcon,
  ShopIcon,
} from "./icons";

// Patient idle policy. 15 min matches Epic MyChart / Athenahealth / industry
// norm for patient portals. Admin surface uses a stricter 5 min in AdminLayout
// because admin blast radius is much larger.
const IDLE_MINUTES = 15;
const IDLE_WARN_SECONDS = 30;

function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="4" y1="6"  x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="6"  y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6"  y2="18" />
    </svg>
  );
}

// Every nav item carries an icon so the sidebar reads as a single
// uniformly-styled set of destinations rather than a wall of text.
// Help is intentionally NOT here — it lives as a dedicated callout card
// at the bottom of the sidebar (see below) for a clearer "you can always
// get help" affordance.
const NAV: ReadonlyArray<{ label: string; to: string; Icon: ComponentType }> = [
  { label: "Dashboard",       to: "/dashboard", Icon: HomeIcon },
  { label: "My Treatment",    to: "/treatment", Icon: PillOutlineIcon },
  { label: "Shots",           to: "/shots",     Icon: SyringeIcon },
  { label: "Messages",        to: "/messages",  Icon: ChatIcon },
  { label: "Progress",        to: "/progress",  Icon: TrendIcon },
  { label: "Visits",          to: "/visits",    Icon: CalendarIcon },
  { label: "Labs",            to: "/labs",      Icon: LabIcon },
  { label: "Shop",            to: "/shop",      Icon: ShopIcon },
  { label: "Billing",         to: "/billing",   Icon: CardIcon },
  { label: "Account",         to: "/account",   Icon: UserIcon },
];

export function AuthLayout({ children, footnote }: { children: ReactNode; footnote?: ReactNode }) {
  return (
    <div className="min-h-screen bg-fh-cream flex flex-col items-center justify-center px-6 py-10 overflow-auto">
      {children}
      {footnote && (
        <p className="mt-[18px] max-w-[380px] text-center text-[11.5px] text-fh-gray">
          {footnote}
        </p>
      )}
    </div>
  );
}

export function AppLayout({
  children,
  initials: initialsOverride,
}: {
  children: ReactNode;
  /**
   * Optional override. If omitted, AppLayout derives initials from the
   * signed-in patient automatically so the avatar appears on every page
   * without the route having to pass it in. usePatient is react-query-
   * cached, so the extra call here is free if the route also queries it.
   */
  initials?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const unreadCountQ = useUnreadMessageCount(patientQ.data?.id);
  const admin = isAdmin(session);
  const [signingOut, setSigningOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);

  // Derive initials from the patient row when the caller didn't pass them.
  // Falls back to the auth email's first character so the avatar never
  // shows as a blank circle.
  const derivedInitials =
    initialsOverride ||
    (patientQ.data
      ? `${patientQ.data.first_name?.[0] ?? ""}${patientQ.data.last_name?.[0] ?? ""}`.toUpperCase()
      : "") ||
    (session?.user.email?.[0]?.toUpperCase() ?? "");
  const initials = derivedInitials || undefined;

  // Auto-close both menus on any route change (nav-link taps, browser
  // back/forward, programmatic navigation).
  useEffect(() => {
    setMenuOpen(false);
    setAvatarMenuOpen(false);
  }, [location.pathname]);

  // Escape closes the desktop avatar dropdown.
  useEffect(() => {
    if (!avatarMenuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setAvatarMenuOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [avatarMenuOpen]);

  // Lock body scroll while the mobile menu is open so the backdrop reads
  // as a true overlay.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  async function handleSignOut() {
    setSigningOut(true);
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  }

  const handleIdleLogout = useCallback(async () => {
    await supabase.auth.signOut();
    navigate("/login", { replace: true, state: { idleLogout: true } });
  }, [navigate]);
  const idle = useIdleTimeout({
    idleMinutes: IDLE_MINUTES,
    warnSecondsBefore: IDLE_WARN_SECONDS,
    onIdle: handleIdleLogout,
  });

  return (
    <div className="min-h-screen bg-fh-hero flex font-sans text-fh-ink">
      {/* Sidebar — sticky so nav stays visible while the main column scrolls.
          Steel grey = the hero gradient's darkest stop, per brand refresh. */}
      <aside className="hidden md:flex w-[236px] shrink-0 bg-fh-steel border-r border-fh-border px-[18px] py-[26px] flex-col sticky top-0 self-start h-screen overflow-y-auto">
        <div className="pb-6 px-1.5">
          <Logo size="md" href="https://fortehealth.co" />
        </div>
        <nav className="flex-1">
          {NAV.map((item) => {
            const Icon = item.Icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={({ isActive }) =>
                  [
                    "flex items-center justify-between px-3 py-[10px] mb-1 rounded-[10px] text-[14px] font-semibold transition",
                    isActive
                      ? "bg-fh-sage text-fh-green-dark"
                      : "text-fh-green-dark hover:bg-fh-sage/40",
                  ].join(" ")
                }
              >
                <span className="flex items-center gap-2.5">
                  {/* Nav icons render gold regardless of link state (brand accent). */}
                  <span className="text-fh-gold"><Icon /></span>
                  {item.label}
                </span>
                {item.to === "/messages" && (unreadCountQ.data ?? 0) > 0 && (
                  <UnreadBadge count={unreadCountQ.data!} />
                )}
              </NavLink>
            );
          })}
        </nav>
        {admin && (
          <NavLink
            to="/admin/users"
            className="block px-3 py-[11px] mb-1 rounded-[10px] text-sm font-semibold text-fh-green-dark bg-fh-sand hover:bg-fh-sage transition mt-2"
          >
            Admin →
          </NavLink>
        )}

        {/* Dedicated help callout. Lives below the main nav so it reads as
            a persistent "you can always get help" promise rather than a
            line-item destination patients scan past. */}
        <NavLink
          to="/help"
          className="block mt-3 rounded-[10px] bg-fh-sage/40 border border-fh-green-light/40 px-3 py-2.5 hover:bg-fh-sage/60 transition"
        >
          <div className="flex items-center gap-2 mb-0.5">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-fh-green-dark text-white">
              <HelpCircleIcon />
            </span>
            <span className="text-[13.5px] font-semibold text-fh-green-dark">
              Need help?
            </span>
          </div>
          <p className="text-[11.5px] text-fh-gray m-0 leading-snug pl-8">
            Browse FAQs or message your care team.
          </p>
        </NavLink>

        <button
          type="button"
          onClick={handleSignOut}
          disabled={signingOut}
          className="px-3 pt-4 mt-2 text-left text-sm font-medium text-fh-gray hover:text-fh-ink border-t border-fh-border disabled:opacity-60"
        >
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-fh-steel border-b border-fh-border h-[56px] px-4 flex items-center justify-between">
        <Logo size="sm" href="https://fortehealth.co" />
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className="-mr-2 p-2 rounded-md text-fh-ink hover:bg-fh-cream"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
        >
          {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>
      </div>

      {/* Mobile menu — backdrop + panel slide down below the top bar */}
      {menuOpen && (
        <>
          <div
            className="md:hidden fixed top-[56px] left-0 right-0 bottom-0 z-20 bg-black/40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <nav
            id="mobile-nav"
            className="md:hidden fixed top-[56px] left-0 right-0 z-30 bg-fh-steel border-b border-fh-border max-h-[calc(100vh-56px)] overflow-auto py-2"
            aria-label="Patient portal navigation"
          >
            {NAV.map((item) => {
              const Icon = item.Icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    [
                      "flex items-center justify-between px-5 py-3 text-[15px] font-semibold transition border-l-4",
                      isActive
                        ? "bg-fh-sage text-fh-green-dark border-fh-green"
                        : "text-fh-green-dark border-transparent hover:bg-fh-sage/30",
                    ].join(" ")
                  }
                >
                  <span className="flex items-center gap-3">
                    <span className="text-fh-gold"><Icon /></span>
                    {item.label}
                  </span>
                  {item.to === "/messages" && (unreadCountQ.data ?? 0) > 0 && (
                    <UnreadBadge count={unreadCountQ.data!} />
                  )}
                </NavLink>
              );
            })}
            {admin && (
              <NavLink
                to="/admin/users"
                className={({ isActive }) =>
                  [
                    "block px-5 py-3 text-[15px] transition border-l-4",
                    isActive
                      ? "bg-fh-sand text-fh-green-dark font-semibold border-fh-green"
                      : "text-fh-green-dark font-semibold border-transparent hover:bg-fh-sand",
                  ].join(" ")
                }
              >
                Admin →
              </NavLink>
            )}
            <div className="border-t border-fh-border my-1" />
            {/* Need-help callout — mobile equivalent of the sidebar callout. */}
            <NavLink
              to="/help"
              className="block mx-4 my-2 rounded-[10px] bg-fh-sage/40 border border-fh-green-light/40 px-3 py-2.5 hover:bg-fh-sage/60 transition"
            >
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-fh-green-dark text-white">
                  <HelpCircleIcon />
                </span>
                <span className="text-[14px] font-semibold text-fh-green-dark">
                  Need help?
                </span>
              </div>
              <p className="text-[12px] text-fh-gray m-0 leading-snug pl-8">
                Browse FAQs or message your care team.
              </p>
            </NavLink>
            <div className="border-t border-fh-border my-1" />
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              className="block w-full text-left px-5 py-3 text-[15px] font-medium text-fh-gray hover:text-fh-ink border-l-4 border-transparent disabled:opacity-60"
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </nav>
        </>
      )}

      {/* Main + footer wrapper. Flex column so the footer pins to the bottom
          of the main column even on short pages. */}
      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 px-5 md:px-9 py-8 md:py-8 pt-[72px] md:pt-8">
          <div className="mx-auto max-w-[1440px]">{children}</div>
        </main>
        <PortalFooter />
      </div>

      {/* Account avatar + dropdown (large screens). The dropdown gives the
          avatar a clear purpose — without it the circle reads as a static
          decoration. Mobile users get the same options from the hamburger. */}
      {initials && (
        <div className="hidden lg:block absolute top-8 right-9">
          <button
            type="button"
            onClick={() => setAvatarMenuOpen((o) => !o)}
            className="w-[42px] h-[42px] rounded-full bg-fh-green-dark text-white flex items-center justify-center text-[15px] font-semibold hover:bg-fh-green transition focus:outline-none focus-visible:ring-2 focus-visible:ring-fh-green focus-visible:ring-offset-2 focus-visible:ring-offset-fh-cream"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={avatarMenuOpen}
            aria-controls="avatar-menu"
          >
            {initials}
          </button>
          {avatarMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setAvatarMenuOpen(false)}
                aria-hidden="true"
              />
              <div
                id="avatar-menu"
                role="menu"
                className="absolute right-0 top-[48px] z-40 w-52 bg-white border border-fh-border rounded-[12px] shadow-card py-1"
              >
                {/* Quick-jump primary nav items. Mirrors the sidebar order so
                    muscle memory carries over from desktop sidebar to the
                    dropdown — patients on smaller laptops or window-snapped
                    browsers may only see this menu. */}
                <AvatarMenuLink to="/dashboard">Dashboard</AvatarMenuLink>
                <AvatarMenuLink to="/treatment">My Treatment</AvatarMenuLink>
                <AvatarMenuLink to="/billing">Billing</AvatarMenuLink>
                <AvatarMenuLink to="/help">Help</AvatarMenuLink>
                <AvatarMenuLink to="/account">Account</AvatarMenuLink>
                {admin && (
                  <>
                    <div className="my-1 border-t border-fh-border" />
                    <NavLink
                      to="/admin/users"
                      role="menuitem"
                      className="block px-3.5 py-2.5 text-[13.5px] text-fh-green-dark font-semibold hover:bg-fh-sand transition"
                    >
                      Admin →
                    </NavLink>
                  </>
                )}
                <div className="my-1 border-t border-fh-border" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="block w-full text-left px-3.5 py-2.5 text-[13.5px] text-fh-gray hover:bg-fh-cream hover:text-fh-ink disabled:opacity-60"
                >
                  {signingOut ? "Signing out…" : "Sign out"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <IdleWarningDialog
        open={idle.warning}
        secondsLeft={idle.secondsLeft}
        onStay={idle.stayActive}
      />
    </div>
  );
}

// Patient-portal footer with the legal-page link inventory. Shown on every
// authenticated route. Keeps the policies always one click away so we can
// truthfully claim "always accessible" in our NPP / Privacy Policy.
function PortalFooter() {
  // The marketing site consolidated its legal inventory to five pages:
  // refunds/accessibility/SMS terms live in Terms; California rights,
  // privacy choices, and cookies live in the Privacy Policy.
  const links: Array<{ label: string; href: string }> = [
    { label: "Privacy Policy",               href: "https://fortehealth.co/privacy" },
    { label: "Notice of Privacy Practices",  href: "https://fortehealth.co/hipaa" },
    { label: "Terms of Service",             href: "https://fortehealth.co/terms" },
    { label: "Telehealth Consent",           href: "https://fortehealth.co/telehealth-consent" },
    { label: "Consumer Health Data Privacy", href: "https://fortehealth.co/consumer-health-data" },
  ];

  return (
    <footer className="mt-auto px-5 md:px-9 pb-8 pt-6 border-t border-fh-border text-fh-gray">
      <div className="mx-auto max-w-[1440px]">
        <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-[12px]">
          {/* In-portal Privacy & data settings page (HIPAA / CCPA actions
              + cookie preferences). Distinct from the marketing-site
              policy links below — this one stays inside the portal. */}
          <li>
            <NavLink
              to="/help"
              className="hover:text-fh-ink underline font-semibold text-fh-ink"
            >
              Help Center
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/account/privacy"
              className="hover:text-fh-ink underline font-semibold text-fh-ink"
            >
              Privacy &amp; data settings
            </NavLink>
          </li>
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-fh-ink underline"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[11.5px] text-fh-gray-light leading-snug">
          ForteHealth is a telehealth platform; medical services are provided by independently
          licensed clinicians. Not for emergencies — call 911. © {new Date().getFullYear()} ForteHealth.
        </p>
      </div>
    </footer>
  );
}

// Small unread count chip next to a nav item. Caps display at "9+" so the
// badge doesn't blow out the sidebar width when the patient has been away.
function UnreadBadge({ count }: { count: number }) {
  const label = count > 9 ? "9+" : String(count);
  return (
    <span
      className="ml-2 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-fh-green text-white text-[11px] font-semibold leading-none"
      aria-label={`${count} unread message${count === 1 ? "" : "s"}`}
    >
      {label}
    </span>
  );
}

// Single dropdown item inside the avatar menu. Highlights when the current
// route matches, same hover/focus styling as the sidebar NavLinks.
function AvatarMenuLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      end
      className={({ isActive }) =>
        [
          "block px-3.5 py-2.5 text-[13.5px] transition",
          isActive
            ? "bg-fh-sage text-fh-green-dark font-semibold"
            : "text-fh-ink hover:bg-fh-cream",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}
